import React, { ReactNode } from "react"
import styled from "styled-components"
import { Banner } from "components/atoms"
import { IconStyleable, IconType } from "components/atoms/Icon"
import Typography from "components/_blueprint/Typography"
import {
  CaseComplexities,
  DebtConsolidation,
  Fees,
  MortgagePreferences,
} from "models/ApplicationSummaryEnhanced"
import { DetailsSection } from "../DetailsSection"
import { ApplicationDataProps } from "pages/Application/Recommendation/types"
import {
  BudgetCopy,
  MortgageTermCopy,
  FeesCopy,
  RepaymentMethodCopy,
  InitialPeriodCopy,
  MortgageTypeCopy,
} from "./copy"

export type RecommendationProductDetails = Pick<
  ApplicationDataProps,
  "initialMonthlyPayment" | "arrangementFee" | "reasonForRecommendation"
>

const mapMortgagePreferences = (
  { initialPeriod, mortgageType, repaymentMethod, term, fees, budget }: MortgagePreferences,
  { initialMonthlyPayment, arrangementFee }: RecommendationProductDetails,
  { isLendingIntoRetirement }: CaseComplexities
): (ReactNode | null)[] => [
  initialPeriod && initialPeriod.period && initialPeriod.customReason ? (
    <InitialPeriodCopy preference={initialPeriod} />
  ) : null,
  budget && budget.amount ? (
    <BudgetCopy budget={budget.amount} monthlyPayment={initialMonthlyPayment} />
  ) : null,
  mortgageType && mortgageType.type && mortgageType.customReason ? (
    <MortgageTypeCopy preference={mortgageType} />
  ) : null,
  repaymentMethod && repaymentMethod.method && repaymentMethod.customReason ? (
    <RepaymentMethodCopy preference={repaymentMethod} />
  ) : null,
  term && term.years && term.customReason ? (
    <MortgageTermCopy preference={term} isLendingIntoRetirement={isLendingIntoRetirement} />
  ) : null,
  // Don't want to display fees if the arrangementFee is 0 and the preference for fees is either PayUpFront or AddToTheLoan
  fees && fees.type && !(arrangementFee === 0 && fees.type !== Fees.NoFees) ? (
    <FeesCopy preference={fees} feeAmount={arrangementFee} />
  ) : null,
]

const mapDebtConsolidation = ({
  customReason,
  debtConsolidationAppropriate,
}: DebtConsolidation): ReactNode | null =>
  debtConsolidationAppropriate ? (
    <>
      We’ve determined that <strong>consolidating your debts</strong> is an appropriate path forward
      because {customReason}
    </>
  ) : null

const RecommendationContainer = styled.div`
  margin: 8px;
  div:not(:last-child) {
    margin-bottom: 8px;
  }
`

const GreenTickContainer = styled.div`
  background-color: #038352;
  height: 24px;
  width: 24px;
  border-radius: 260px;
  gap: 8px;
  padding: 6px;
  display: flex;
  align-items: flex-start;
`

const GreenTick = () => (
  <GreenTickContainer>
    <IconStyleable fillDefault="#FFFFFF" height={12} width={12} type={IconType.WhiteTick} />
  </GreenTickContainer>
)

interface RecommendationReasonProps {
  children: ReactNode
}

const RecommendationReason = ({ children }: RecommendationReasonProps) => {
  return (
    <Banner fullWidth variant="filled" colour="success" leftIcon={<GreenTick />}>
      <Typography.SmallBody data-testid="reason-for-recommendation" color="success">
        {children}
      </Typography.SmallBody>
    </Banner>
  )
}

interface ReasonsForRecommendationsProps {
  mortgagePreferences: MortgagePreferences
  productDetails: RecommendationProductDetails
  caseComplexities: CaseComplexities
  debtConsolidation?: DebtConsolidation
}

export const ReasonsForRecommendation = ({
  mortgagePreferences,
  debtConsolidation,
  caseComplexities,
  productDetails,
}: ReasonsForRecommendationsProps) => {
  const mappedReasonings = mapMortgagePreferences(
    mortgagePreferences,
    productDetails,
    caseComplexities
  )
  if (debtConsolidation) mappedReasonings.push(mapDebtConsolidation(debtConsolidation))
  if (productDetails?.reasonForRecommendation)
    mappedReasonings.unshift(<>{productDetails.reasonForRecommendation}</>)

  const reasonings = mappedReasonings.filter((reason) => reason)
  if (reasonings.length === 0) return null

  return (
    <DetailsSection title="Why we've recommended this deal">
      <RecommendationContainer>
        {reasonings.map((reason, index) => (
          <RecommendationReason key={index}>{reason}</RecommendationReason>
        ))}
      </RecommendationContainer>
    </DetailsSection>
  )
}
